// ==========================================================================
// screens/Profile.tsx — My profile (FF-058, 10 Oct 2026)
//   James: "we want to add a user profile to input details and change theme".
//   Your details (on your own people row, 035), your colours (per login),
//   light or dark (per device), and the app itself: Update app and Fresh start.
// ==========================================================================
import { useEffect, useState } from "react";
import { Check, Fingerprint, KeyRound, Laptop, Loader2, LogOut, Moon, RotateCcw, Share, ShieldCheck, Sun, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/Page";
import { Card, ErrorText } from "@/components/ui";
import { Lbl, inputCls } from "@/components/Sheet";
import Emblem from "@/components/Emblem";
import { UpdateApp } from "@/components/AccountMenu";
import { useAuth } from "@/lib/auth";
import { PERSON_COLOURS, THEMES, useMe, useMembership, useSaveProfile, useSetTheme, type Theme } from "@/lib/household";
import { tokenColour } from "@/lib/calendar";
import { MODES, useMode, type Mode } from "@/lib/appearance";
import { freshStart } from "@/lib/pwa";
import { addPasskey, canUsePasskeys, listFactors, removeFactor, type Factor } from "@/lib/quickSignIn";
import { supabase } from "@/lib/supabase";

const MODE_ICON: Record<Mode, typeof Sun> = { auto: Laptop, light: Sun, dark: Moon };

/** A little picture of the app in that theme: sidebar, cream page, button, crest. */
function ThemeCard({ t, active, onPick }: { t: (typeof THEMES)[number]; active: boolean; onPick: () => void }) {
  return (
    <button type="button" onClick={onPick} aria-pressed={active} data-theme-preview={t.key}
      className={`group relative overflow-hidden rounded-lg text-left ring-1 transition ${active ? "ring-2 ring-ink" : "ring-line hover:-translate-y-0.5 hover:ring-primary/50"}`}>
      {/* The preview sets the theme on itself so its colours are that theme's, whatever is on now. */}
      <div data-theme={t.key} className="theme-scope flex h-24">
        <div className="flex w-9 flex-col items-center gap-1.5 pt-2.5" style={{ background: "rgb(var(--panel))" }}>
          <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "rgb(var(--cream))" }}><Emblem className="h-5 w-5" /></span>
          <span className="h-1.5 w-5 rounded-full" style={{ background: "rgb(var(--primary))" }} />
          <span className="h-1.5 w-5 rounded-full opacity-40" style={{ background: "rgb(var(--panel-muted))" }} />
          <span className="h-1.5 w-5 rounded-full opacity-40" style={{ background: "rgb(var(--panel-muted))" }} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-2.5" style={{ background: "rgb(var(--bg))" }}>
          <span className="h-2 w-14 rounded-full" style={{ background: "rgb(var(--text) / 0.75)" }} />
          <span className="flex-1 rounded-md" style={{ background: "rgb(var(--surface))", boxShadow: "0 0 0 1px rgb(var(--border))" }} />
          <span className="h-3.5 w-12 rounded-full" style={{ background: "rgb(var(--primary))" }} />
        </div>
      </div>
      <div className="flex items-center justify-between bg-surface px-3 py-2">
        <span className="text-sm font-semibold">{t.label}</span>
        {active ? <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ink text-surface"><Check className="h-3 w-3" /></span> : <span className="h-3 w-3 rounded-full" style={{ background: t.swatch }} />}
      </div>
    </button>
  );
}

/** Change password (FF-059): so a login James set up with a starting password can be made your own. */
function ChangePassword() {
  const [open, setOpen] = useState(false); const [a, setA] = useState(""); const [b, setB] = useState("");
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const ok = a.length >= 10 && a === b;
  const go = async () => {
    setBusy(true); setMsg(null);
    const { error } = await supabase.auth.updateUser({ password: a });
    setBusy(false);
    if (error) setMsg({ ok: false, text: /reauth|recent/i.test(error.message) ? "For safety, sign out and back in, then try again." : error.message });
    else { setMsg({ ok: true, text: "Password changed. Use the new one next time you sign in." }); setA(""); setB(""); setOpen(false); }
  };
  return (
    <div>
      <button type="button" onClick={() => { setOpen((o) => !o); setMsg(null); }} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] hover:bg-surface-raised">
        <KeyRound className="h-[18px] w-[18px] text-ink-muted" />
        <span><span className="block font-medium">Change password</span><span className={`block text-xs ${msg?.ok ? "text-good" : "text-ink-muted"}`}>{msg?.ok ? msg.text : "At least 10 characters."}</span></span>
      </button>
      {open && (
        <div className="space-y-3 rounded-md bg-surface-raised p-3">
          <input className={inputCls} type="password" autoComplete="new-password" placeholder="New password" value={a} onChange={(e) => setA(e.target.value)} />
          <input className={inputCls} type="password" autoComplete="new-password" placeholder="The same again" value={b} onChange={(e) => setB(e.target.value)} />
          {b && a !== b && <p className="text-xs text-bad">The two don't match.</p>}
          {msg && !msg.ok && <p className="text-xs text-bad">{msg.text}</p>}
          <button type="button" disabled={!ok || busy} onClick={go} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 font-semibold text-primary-ink disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Change it</button>
        </div>
      )}
    </div>
  );
}

/** Quick sign-in (FF-061): Face ID / fingerprint on this device instead of typing the code. */
function QuickSignIn() {
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => void listFactors().then(setFactors);
  useEffect(load, []);
  const passkeys = (factors ?? []).filter((f) => f.type === "webauthn" && f.verified);
  const add = async () => {
    setBusy(true); setMsg(null);
    const r = await addPasskey();
    setBusy(false);
    setMsg(r.ok ? { ok: true, text: "Done. Next time, the second step is one tap." } : { ok: false, text: r.message });
    load();
  };
  const remove = async (id: string) => { setBusy(true); try { await removeFactor(id); } catch (e) { setMsg({ ok: false, text: (e as Error).message }); } setBusy(false); load(); };
  return (
    <Card className="space-y-3">
      <div className="flex items-start gap-3">
        <Fingerprint className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[17px] font-semibold">Quick sign-in</h2>
          <p className="text-sm text-ink-muted">Add Face ID (or fingerprint) on this device so the second step is one tap instead of a code. You stay signed in between visits; this is for the times the app does ask.</p>
        </div>
      </div>
      {passkeys.length > 0 && (
        <ul className="space-y-1.5">
          {passkeys.map((f) => (
            <li key={f.id} className="flex items-center gap-3 rounded-md bg-surface-raised px-3 py-2 text-sm">
              <Check className="h-4 w-4 text-good" /><span className="flex-1">{f.name ?? "Face ID"}</span>
              <button type="button" onClick={() => remove(f.id)} disabled={busy} aria-label="Remove" className="rounded-full p-1 text-ink-muted hover:text-bad"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {canUsePasskeys() ? (
        <button type="button" onClick={add} disabled={busy} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 font-semibold text-primary-ink disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}{passkeys.length ? "Add another device" : "Add Face ID on this device"}
        </button>
      ) : <p className="text-xs text-ink-muted">This browser can't do Face ID or fingerprint sign-in.</p>}
      {msg && <p className={`text-sm ${msg.ok ? "text-good" : "text-bad"}`}>{msg.text}</p>}
      <p className="text-xs text-ink-muted">The code from your authenticator app always works as well.</p>
    </Card>
  );
}

export default function Profile() {
  const { session, signOut } = useAuth();
  const me = useMe(); const m = useMembership(); const save = useSaveProfile(); const setTheme = useSetTheme();
  const [mode, setMode] = useMode();
  const [f, setF] = useState({ display_name: "", full_name: "", mobile: "", born_on: "", colour: "chart-1", wfh: "8" });
  const [saved, setSaved] = useState(false); const [err, setErr] = useState(""); const [sure, setSure] = useState(false);

  useEffect(() => {
    if (!me) return;
    setF({ display_name: me.display_name, full_name: me.full_name ?? "", mobile: me.mobile ?? "", born_on: me.born_on ?? "", colour: me.colour, wfh: String(me.wfh_default_hours) });
  }, [me?.id, me?.display_name, me?.full_name, me?.mobile, me?.born_on, me?.colour, me?.wfh_default_hours]);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => { setF({ ...f, [k]: e.target.value }); setSaved(false); };
  const mobileOk = !f.mobile.trim() || /^[0-9 +()-]{6,20}$/.test(f.mobile.trim());
  const wfh = Number(f.wfh);
  const ok = f.display_name.trim() && mobileOk && wfh > 0 && wfh <= 24;

  const submit = () => {
    setErr("");
    save.mutate({
      display_name: f.display_name.trim(), full_name: f.full_name.trim() || null, mobile: f.mobile.trim() || null,
      born_on: f.born_on || null, colour: f.colour, wfh_default_hours: wfh,
    }, { onSuccess: () => setSaved(true), onError: (e) => setErr((e as Error).message) });
  };

  const theme: Theme = m.data?.theme ?? "rose";

  return (
    <>
      <PageHeader title="My profile" subtitle="Your details, your colours, and this device's settings. Everyone in the family has their own." />

      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="space-y-5 self-start lg:col-span-7">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full font-heading text-2xl font-semibold text-white shadow-soft" style={{ background: tokenColour(f.colour) }}>{f.display_name.trim()[0] ?? "·"}</span>
            <div className="min-w-0">
              <h2 className="font-heading text-2xl font-semibold leading-tight">{f.full_name.trim() || f.display_name || "You"}</h2>
              <p className="truncate text-sm text-ink-muted">{session?.user.email}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Lbl label="Name" hint="Shown around the app."><input className={inputCls} value={f.display_name} onChange={set("display_name")} /></Lbl>
            <Lbl label="Full name"><input className={inputCls} value={f.full_name} onChange={set("full_name")} placeholder="First and last" /></Lbl>
            <Lbl label="Mobile"><input className={inputCls} value={f.mobile} onChange={set("mobile")} inputMode="tel" placeholder="04xx xxx xxx" />{!mobileOk && <span className="block text-xs text-bad">Numbers, spaces, + ( ) and - only.</span>}</Lbl>
            <Lbl label="Birthday"><input type="date" className={inputCls} value={f.born_on} onChange={set("born_on")} /></Lbl>
            {me?.role === "adult" && <Lbl label="Work from home hours a day" hint="Fills in a WFH day."><input className={`${inputCls} num`} value={f.wfh} onChange={set("wfh")} inputMode="decimal" /></Lbl>}
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-ink-muted">Your colour <span className="font-normal">· on the calendar, tasks and lists</span></div>
            <div className="flex flex-wrap gap-3">
              {PERSON_COLOURS.map(({ key: c, label }) => (
                <button key={c} type="button" aria-label={label} title={label} aria-pressed={f.colour === c} onClick={() => { setF({ ...f, colour: c }); setSaved(false); }}
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-white ring-offset-2 ring-offset-surface transition ${f.colour === c ? "ring-2 ring-ink" : "hover:scale-105"}`} style={{ background: tokenColour(c) }}>
                  {f.colour === c && <Check className="h-4 w-4" />}
                </button>
              ))}
            </div>
          </div>

          <ErrorText>{err}</ErrorText>
          <div className="flex items-center gap-3 border-t border-line pt-5">
            <button type="button" onClick={submit} disabled={!ok || save.isPending} className="flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-semibold text-primary-ink disabled:opacity-50">
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
            {saved && <span className="flex items-center gap-1.5 text-sm text-good"><Check className="h-4 w-4" /> Saved</span>}
          </div>
        </Card>

        <div className="space-y-6 lg:col-span-5">
          <Card>
            <h2 className="text-[17px] font-semibold">Colours</h2>
            <p className="mb-4 text-sm text-ink-muted">Just for your login, on every device. All of them sit on the same cream.</p>
            <div className="grid grid-cols-2 gap-3">
              {THEMES.map((t) => <ThemeCard key={t.key} t={t} active={theme === t.key} onPick={() => setTheme.mutate(t.key)} />)}
            </div>
          </Card>

          <Card>
            <h2 className="text-[17px] font-semibold">Light or dark</h2>
            <p className="mb-4 text-sm text-ink-muted">On this device only. Auto follows the phone's own setting.</p>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((x) => {
                const Icon = MODE_ICON[x.key];
                return (
                  <button key={x.key} type="button" onClick={() => setMode(x.key)} aria-pressed={mode === x.key}
                    className={`flex flex-col items-center gap-1.5 rounded-md px-2 py-3 text-sm font-semibold transition ${mode === x.key ? "bg-primary text-primary-ink shadow-soft" : "bg-surface-raised text-ink-muted hover:text-ink"}`}>
                    <Icon className="h-5 w-5" />{x.label}
                  </button>
                );
              })}
            </div>
          </Card>

          <QuickSignIn />

          <Card className="space-y-1 p-3">
            <h2 className="px-3 pt-2 text-[17px] font-semibold">The app</h2>
            <UpdateApp />
            {!sure ? (
              <button type="button" onClick={() => setSure(true)} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] hover:bg-surface-raised">
                <RotateCcw className="h-[18px] w-[18px] text-ink-muted" />
                <span><span className="block font-medium">Fresh start</span><span className="block text-xs text-ink-muted">If something looks stuck: reloads the app from scratch.</span></span>
              </button>
            ) : (
              <div className="rounded-md bg-surface-raised p-3 text-sm">
                <p>This clears the app's saved copy on this device and loads it again. Your family's data isn't touched, and you stay signed in.</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => void freshStart()} className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-ink">Fresh start</button>
                  <button type="button" onClick={() => setSure(false)} className="rounded-md px-4 py-2 text-ink-muted">Cancel</button>
                </div>
              </div>
            )}
            <div className="flex gap-3 px-3 py-2.5 text-[15px]">
              <Share className="mt-0.5 h-[18px] w-[18px] shrink-0 text-ink-muted" />
              <span><span className="block font-medium">On the iPhone home screen</span><span className="block text-xs text-ink-muted">In Safari: the Share button, then Add to Home Screen.</span></span>
            </div>
            <ChangePassword />
            <div className="flex gap-3 px-3 py-2.5 text-[15px]">
              <ShieldCheck className="mt-0.5 h-[18px] w-[18px] shrink-0 text-good" />
              <span><span className="block font-medium">Two-step sign-in is on</span><span className="block text-xs text-ink-muted">Every sign-in asks for the code from your authenticator app.</span></span>
            </div>
            <button type="button" onClick={signOut} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] text-ink-muted hover:bg-surface-raised hover:text-ink"><LogOut className="h-[18px] w-[18px]" /> Sign out</button>
          </Card>
        </div>
      </div>
    </>
  );
}
